<template>
    <IonPage>
        <div
            class="relative h-screen flex flex-col items-center justify-center p-6 pt-(--beyond-safe-area-inset-top)! pb-(--beyond-safe-area-inset-bottom)!"
            :style="{ backgroundColor }"
            data-cy="login"
        >
            <UILoading
                v-if="browserAuthLoading"
                class="w-12 h-12"
            />

            <div
                v-if="loginAbortHandler"
                v-show="!browserAuthLoading"
                class="absolute right-0 top-(--beyond-safe-area-inset-top) p-2"
            >
                <DButton
                    icon="x-mark"
                    theme="transparent"
                    :background="useDarkText ? null : 'invert'"
                    @click="goBack"
                />
            </div>

            <div
                v-if="logoUrl"
                v-show="!browserAuthLoading"
                class="px-12"
            >
                <img
                    :src="logoUrl"
                    alt="logo"
                    class="max-w-40"
                >
            </div>

            <div
                v-show="!browserAuthLoading"
                :class="{ 'bg-white': type !== authTypes.url }"
                class="rounded-lg px-4 py-6 w-full mt-12 flex flex-col items-center max-w-md"
            >
                <p
                    v-if="version.login_help_text"
                    class="mb-6 text-sm break-words max-w-full text-center"
                >
                    {{ version.login_help_text }}
                </p>
                <UrlLogin
                    v-if="type === authTypes.url"
                    :auth-requirements="authRequirements"
                    :loading="loading"
                    @loading="loading = $event"
                />
                <BasicLogin
                    v-else-if="type === authTypes.basic"
                    :auth-requirements="authRequirements"
                    :loading="loading"
                    @loading="loading = $event"
                />
                <FireflyLogin
                    v-else-if="type === authTypes.firefly"
                    :auth-requirements="authRequirements"
                    :loading="loading"
                    @loading="loading = $event"
                />
                <div v-else>
                    <DAlert
                        type="danger"
                        title="Error"
                    >
                        Unexpected authentication type, something has gone wrong
                    </DAlert>
                </div>
            </div>

            <div
                class="absolute bottom-(--beyond-safe-area-inset-bottom) right-0 px-2 py-2 text-xs flex items-center justify-end"
                :class="useDarkText ? 'text-gray-800' : 'text-white'"
            >
                <span
                    class="w-8 h-8 flex items-center justify-center"
                    @click="openSettings"
                >
                    <DIcon
                        icon="cog-6-tooth"
                        class="flex!"
                        size="w-6 h-6"
                    />
                </span>
            </div>
        </div>
    </IonPage>
</template>

<script setup>
    import tinycolor from 'tinycolor2'
    import {
        DIcon, DAlert, DButton,
    } from '@digistorm/spark'
    import { isAxiosError } from 'axios'
    import { statusBar } from '@/services'
    import authTypes from '@/constants/authTypes'
    import FireflyLogin from '@/components/login/FireflyLogin.vue'
    import BasicLogin from '@/components/login/BasicLogin.vue'
    import UrlLogin from '@/components/login/UrlLogin.vue'
    import { usePlatformAuthStore } from '@/stores/platformAuth'
    import { usePlatformAuthUrlStore } from '@/stores/platformAuthUrl'

    const appStore = useAppStore()
    const platformAuthStore = usePlatformAuthStore()
    const platformAuthUrlStore = usePlatformAuthUrlStore()
    const moduleStore = useModuleStore()
    const versionStore = useVersionStore()

    const moduleHelpers = useModuleHelpers()
    const { homepageAnimation } = useAnimations()

    const router = useIonRouter()

    const { appId, mockData } = storeToRefs(appStore)
    const { authRequirements, loginSuccessHandler, loginAbortHandler } = storeToRefs(platformAuthStore)
    const { browserAuthLoading } = storeToRefs(platformAuthUrlStore)
    const { version } = storeToRefs(versionStore)

    const { snackbar } = useSnackbars()

    const type = computed(() => {
        return authRequirements.value.type
    })

    const loading = ref(false)

    const backgroundColor = computed(() => {
        const backgroundColor = get(version.value, 'background_color')
        if (isEmpty(backgroundColor)) {
            return '#ffffff'
        }
        return backgroundColor
    })

    const useDarkText = computed(() => {
        const color = tinycolor(backgroundColor.value)
        return color.isLight()
    })

    const logoUrl = computed(() => {
        return get(version.value, 'logo_file.url')
    })

    const goBack = () => {
        loginAbortHandler.value?.()
    }

    const handleLogin = (event) => {
        loginSuccessHandler.value?.(event)
    }

    const handleError = (e) => {
        loading.value = false

        if (isAxiosError(e) && get(e, 'response.status') === 422) {
            return
        }

        if (moduleHelpers.isActionException(e)) {
            moduleHelpers.handleActionException(router, e.response.data)
        } else {
            snackbar({
                message: 'Unexpected error',
                type: 'error',
            })
        }
    }

    const handleAbort = (e) => {
        loading.value = false
    }

    const openSettings = () => {
        // set active module url
        moduleStore.addModule({
            id: 'settings',
            name: 'Settings',
            settings: [],
            link: {
                type: 'internal',
                url: `/${appId.value}/settings`,
            },
        })

        router.push({ name: 'view', params: { id: 'settings' } }, homepageAnimation)
    }

    onMounted(() => {
        platformAuthStore.events.on('login', handleLogin)
        platformAuthStore.events.on('error', handleError)
        platformAuthStore.events.on('abort', handleAbort)

        statusBar.setTheme(backgroundColor.value)
    })

    onUnmounted(() => {
        platformAuthStore.events.off('login', handleLogin)
        platformAuthStore.events.off('error', handleError)
        platformAuthStore.events.off('abort', handleAbort)
    })

    onIonViewWillEnter(() => {
        // Try handle login if mockdata is enabled
        if (mockData.value) {
            handleLogin({
                userSwitched: true,
            })
        }
    })
</script>
